import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import patientService from "../../services/patientService";

// Async thunks
export const fetchPatients = createAsyncThunk(
  "patients/fetchPatients",
  async ({ doctorId, page = 1, limit = 10 }) => {
    return await patientService.getPatients(doctorId, page, limit);
  },
);

export const fetchPatientById = createAsyncThunk(
  "patients/fetchPatientById",
  async (id) => {
    return await patientService.getPatientById(id);
  },
);

export const addPatient = createAsyncThunk(
  "patients/addPatient",
  async (patientData, { rejectWithValue }) => {
    try {
      return await patientService.createPatient(patientData);
    } catch (error) {
      // Extract error message from backend response
      const errorMessage = error?.error || error?.message || "Failed to create patient";
      return rejectWithValue(errorMessage);
    }
  },
);

export const updatePatientData = createAsyncThunk(
  "patients/updatePatientData",
  async ({ id, patientData }) => {
    return await patientService.updatePatient(id, patientData);
  },
);

export const removePatient = createAsyncThunk(
  "patients/removePatient",
  async (id) => {
    await patientService.deletePatient(id);
    return id;
  },
);

export const searchPatients = createAsyncThunk(
  "patients/searchPatients",
  async ({ query, doctorId }) => {
    return await patientService.searchPatients(query, doctorId);
  },
);

const patientSlice = createSlice({
  name: "patients",
  initialState: {
    patients: [],
    selectedPatient: null,
    loading: false,
    error: null,
    successMessage: null,
    pagination: {
      total: 0,
      page: 1,
      limit: 10,
      pages: 0,
    },
  },
  reducers: {
    clearSelectedPatient: (state) => {
      state.selectedPatient = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch patients
    builder
      .addCase(fetchPatients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatients.fulfilled, (state, action) => {
        state.loading = false;
        state.patients = action.payload.data || action.payload;
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchPatients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    // Fetch patient by ID
    builder
      .addCase(fetchPatientById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatientById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedPatient = action.payload;
      })
      .addCase(fetchPatientById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    // Add patient
    builder
      .addCase(addPatient.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(addPatient.fulfilled, (state, action) => {
        state.loading = false;
        state.patients.push(action.payload);
        state.successMessage = "Patient added successfully!";
        state.error = null;
      })
      .addCase(addPatient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message || "Failed to create patient";
        state.successMessage = null;
      });

    // Update patient
    builder
      .addCase(updatePatientData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePatientData.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.patients.findIndex(
          (p) => p.id === action.payload.id,
        );
        if (index !== -1) {
          state.patients[index] = action.payload;
        }
      })
      .addCase(updatePatientData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    // Delete patient
    builder
      .addCase(removePatient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removePatient.fulfilled, (state, action) => {
        state.loading = false;
        state.patients = state.patients.filter((p) => p.patient_id !== action.payload);
      })
      .addCase(removePatient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    // Search patients
    builder
      .addCase(searchPatients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchPatients.fulfilled, (state, action) => {
        state.loading = false;
        state.patients = action.payload;
      })
      .addCase(searchPatients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { clearSelectedPatient, clearSuccessMessage } =
  patientSlice.actions;
export default patientSlice.reducer;
